import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	FormControl,
	FormHelperText,
	Grid,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { addNewUser, sentVerificationEmail } from 'store/user-service/asyncActions';
import { getUser } from 'store/user-service/selector';
import { removeSignUpError } from 'store/user-service/userSlice';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { errorNotify, successNotify } from 'core/helpers/toasts';
import { signUpSchema } from 'core/helpers/yupSchemas';
import { SignUpType } from 'core/types/base';

import { LoginHeader } from 'components/Header/LoginHeader';

import { ReactComponent as EyeSlash } from 'assets/icons/eye-slash.svg';

import { ErrorVerifyEmailModal } from './ErrorVerifyEmailModal';

export const SignUp = () => {
	const { t: signUpPageTranslate } = useTranslation(NamespacesList['signUpPage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const { t: modalsTranslate } = useTranslation(NamespacesList['modals']);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const userData = useSelector(getUser);

	const [openErrorVerify, setOpenErrorVerify] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [userEmail, setUserEmail] = useState('');

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const {
		handleSubmit,
		register,
		formState: { errors },
		watch,
		resetField,
		getValues,
		reset,
	} = useForm({
		resolver: yupResolver(signUpSchema()),
	});

	const stringHasWhitespaceAtTheBeginning = /^\s/;

	useEffect(() => {
		const subscription = watch((value, { name }) => {
			if (name) {
				if (stringHasWhitespaceAtTheBeginning.test(String(value[name]))) {
					resetField(name);
				}
			}
		});
		return () => subscription.unsubscribe();
	}, [watch]);

	useEffect(() => {
		dispatch(removeSignUpError());
	}, [watch('email')]);

	const redirectToLogin = () => {
		navigate(ROUTES.LOGIN);
	};

	const handleActionErrorVerifyModal = () => {
		setOpenErrorVerify((prev) => !prev);
	};

	const handleVerifyEmail = async () => {
		await dispatch(
			sentVerificationEmail({
				redirectLink: window.location.href.replace('sign-up', 'sign-in'),
				userEmail: getValues().email || userEmail,
				toastifyTitle: modalsTranslate('verifyEmail.title') || '',
				toastifyDescription: modalsTranslate('verifyEmail.description') || '',
				errorFunction: handleActionErrorVerifyModal,
			})
		);
	};

	const handleStartVerify = () => {
		if (getValues().email) {
			setUserEmail(getValues().email);
			handleVerifyEmail();
		}
		reset();
	};

	const handleRestartVerify = () => {
		if (userEmail) {
			handleActionErrorVerifyModal();
			handleVerifyEmail();
		}
	};

	const registerUser = async (data: SignUpType) => {
		// use regular data instead of updatedData once reverting changes
		const updatedData: SignUpType = {
			...data,
			department: null,
			organization: null,
			phoneNumber: null,
		};

		try {
			// @ts-ignore
			const { errors, data } = await dispatch(addNewUser(updatedData)).unwrap();

			if (!userData.signUpError && !errors) {
				successNotify(
					modalsTranslate('successfulSignUpModal.title'),
					modalsTranslate('successfulSignUpModal.description')
				);
				handleStartVerify();
			} else {
				if (data) errorNotify('error', data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const handlePolicyRedirect = () => {
		window.open('https://ellogy.ai/privacy-policy/', '_blank');
	};

	const handleTermOfUseRedirect = () => {
		window.open('https://ellogy.ai/terms-of-use/', '_blank');
	};

	return (
		<>
			<LoginHeader />

			<Box
				className="rtl-able"
				sx={{
					display: 'flex',
					justifyContent: 'center',
					height: { xs: 'unset', sm: 'calc(100vh - 124px)' },
					alignItems: 'center',
				}}
			>
				<Grid
					container
					sx={{ width: { xs: '90%', sm: '574px' }, margin: { xs: '16px 0', sm: 0 }, gap: '24px' }}
				>
					<Grid item sx={{ width: '100%', textAlign: 'center' }}>
						<Typography variant="h3" sx={{ fontSize: '34px', fontWeight: '700' }}>
							{signUpPageTranslate('text.signUp')}
						</Typography>
					</Grid>

					<Grid item sx={{ width: '100%' }}>
						<form onSubmit={handleSubmit(registerUser)}>
							<Grid container direction="column">
								<Grid item>
									<Grid
										container
										spacing={2.4}
										sx={{
											display: 'flex',
											flexDirection: { xs: 'column', sm: 'row' },
										}}
									>
										<Grid item sx={{ width: { xs: '100%', sm: '50%' } }}>
											<FormControl fullWidth error={!!errors.firstName}>
												<Typography sx={{ fontSize: '12px' }}>
													{signUpPageTranslate('text.firstName')}
												</Typography>
												<TextField
													inputProps={{ style: { padding: '10px 12px' } }}
													placeholder={signUpPageTranslate('placeholders.firstName') as string}
													sx={{
														height: '44px',
														'& .MuiOutlinedInput-root': {
															borderRadius: '8px',
														},
													}}
													{...register('firstName')}
												/>
												<FormHelperText
													sx={{
														fontSize: '12px',
														textAlign: 'right',
														marginRight: '12px',
														marginTop: '0',
													}}
												>
													{errors.firstName?.message}
												</FormHelperText>
											</FormControl>
										</Grid>

										{/*Delete after uncommenting original last name field*/}
										<Grid item sx={{ width: { xs: '100%', sm: '50%' } }}>
											<FormControl fullWidth error={!!errors.lastName}>
												<Typography sx={{ fontSize: '12px' }}>
													{signUpPageTranslate('text.lastName')}
												</Typography>
												<TextField
													inputProps={{ style: { padding: '10px 12px' } }}
													placeholder={signUpPageTranslate('placeholders.lastName') as string}
													sx={{
														height: '44px',
														'& .MuiOutlinedInput-root': {
															borderRadius: '8px',
														},
													}}
													{...register('lastName')}
												/>
												<FormHelperText
													sx={{
														fontSize: '12px',
														textAlign: 'right',
														marginRight: '12px',
														marginTop: '0',
													}}
												>
													{errors.lastName?.message}
												</FormHelperText>
											</FormControl>
										</Grid>
									</Grid>
								</Grid>

								<Grid item mt={errors.lastName || errors.department ? 0 : 2.4}>
									<Grid
										container
										spacing={2.4}
										sx={{
											display: 'flex',
											flexDirection: { xs: 'column', sm: 'row' },
										}}
									>
										<Grid item sx={{ width: { xs: '100%', sm: '50%' } }}>
											<FormControl fullWidth error={!!errors.email}>
												<Typography sx={{ fontSize: '12px' }}>
													{signUpPageTranslate('text.email')}
												</Typography>
												<TextField
													inputProps={{ style: { padding: '10px 12px' } }}
													placeholder="example@gmail.com"
													sx={{
														height: '44px',
														'& .MuiOutlinedInput-root': {
															borderRadius: '8px',
														},
													}}
													{...register('email')}
												/>
												{userData.signUpError ? (
													<FormHelperText
														sx={{
															fontSize: '12px',
															textAlign: 'right',
															marginRight: '12px',
															marginTop: '0',
															color: '#FB0B24',
														}}
													>
														{signUpPageTranslate('text.userWithThisEmailAlreadyExists')}
													</FormHelperText>
												) : (
													<FormHelperText
														sx={{
															fontSize: '12px',
															textAlign: 'right',
															marginRight: '12px',
															marginTop: '0',
														}}
													>
														{errors.email?.message}
													</FormHelperText>
												)}
											</FormControl>
										</Grid>

										<Grid item sx={{ width: { xs: '100%', sm: '50%' } }}>
											<FormControl fullWidth error={!!errors.password}>
												<Typography sx={{ fontSize: '12px' }}>
													{signUpPageTranslate('text.password')}
												</Typography>
												<Box
													sx={{
														position: 'relative',
														'&:hover::after': {
															content:
																'"Password requirements: ' +
																'\\A 1. At least 12 characters long ' +
																'\\A 2. At least one digit ' +
																'\\A 3. At least one uppercase letter ' +
																'\\A 4. At least one lowercase letter ' +
																'\\A 5. At least one special character (symbol)"',
															position: 'absolute',
															top: 'calc(100% + 8px)',
															left: 0,
															backgroundColor: '#f5f5f5',
															color: '#333',
															fontSize: '12px',
															lineHeight: '1.5',
															padding: '12px',
															borderRadius: '4px',
															whiteSpace: 'pre-wrap',
															zIndex: 1,
															width: '100%',
															boxSizing: 'border-box',
															boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
															border: '1px solid #e0e0e0',
														},
													}}
												>
													<TextField
														InputProps={{
															endAdornment: (
																<InputAdornment position="end">
																	<Button
																		disableRipple
																		sx={{
																			minWidth: 0,
																			'&:hover': {
																				backgroundColor: '#FFF',
																			},
																		}}
																		onClick={handleClickShowPassword}
																	>
																		<EyeSlash />
																	</Button>
																</InputAdornment>
															),
														}}
														inputProps={{
															style: {
																padding: '10px 12px',
																borderRadius: '8px',
															},
														}}
														type={showPassword ? 'text' : 'password'}
														placeholder={signUpPageTranslate('placeholders.password') as string}
														sx={{
															height: '44px',
															'& .MuiOutlinedInput-root': {
																borderRadius: '8px',
															},
															width: '100%', // Ensure the TextField takes full width
														}}
														{...register('password')}
													/>
												</Box>
												<FormHelperText
													sx={{
														fontSize: '12px',
														textAlign: 'right',
														marginRight: '12px',
														marginTop: '0',
													}}
												>
													{errors.password?.message}
												</FormHelperText>
											</FormControl>
										</Grid>
									</Grid>
								</Grid>

								<Grid item mt={errors.email || errors.password || userData.signUpError ? 0 : 2.4}>
									<Grid
										container
										spacing={2.4}
										sx={{
											display: 'flex',
											flexDirection: { xs: 'column', sm: 'row' },
										}}
									>
										<Grid item sx={{ width: { xs: '100%', sm: '50%' } }}>
											<FormControl fullWidth error={!!errors.confirmPassword}>
												<Typography sx={{ fontSize: '12px' }}>
													{signUpPageTranslate('text.confirmPassword')}
												</Typography>
												<TextField
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<Button
																	disableRipple
																	sx={{
																		minWidth: 0,
																		'&:hover': {
																			backgroundColor: '#FFF',
																		},
																	}}
																	onClick={handleClickShowConfirmPassword}
																>
																	<EyeSlash />
																</Button>
															</InputAdornment>
														),
													}}
													inputProps={{
														style: {
															padding: '10px 12px',
															borderRadius: '8px',
														},
													}}
													type={showConfirmPassword ? 'text' : 'password'}
													placeholder={signUpPageTranslate('placeholders.password') as string}
													sx={{
														height: '44px',
														'& .MuiOutlinedInput-root': {
															borderRadius: '8px',
														},
													}}
													{...register('confirmPassword')}
												/>
												<FormHelperText
													sx={{
														fontSize: '12px',
														textAlign: 'right',
														marginRight: '12px',
														marginTop: '0',
													}}
												>
													{errors.confirmPassword?.message}
												</FormHelperText>
											</FormControl>
										</Grid>
									</Grid>
								</Grid>

								<Grid item mt={2.4}>
									<FormControl error={!!errors.check}>
										<Box sx={{ display: 'flex', gap: '8px' }}>
											<input type="checkbox" style={{ margin: '0' }} {...register('check')} />

											<Typography sx={{ fontSize: '12px', marginLeft: '8px' }}>
												{signUpPageTranslate('text.policyTextPartOne')}{' '}
												<span
													style={{ color: '#4786FF', cursor: 'pointer' }}
													onClick={handleTermOfUseRedirect}
												>
													{signUpPageTranslate('text.policyTextPartTwo')}
												</span>{' '}
												{signUpPageTranslate('text.policyTextPartThree')}{' '}
												<span
													style={{ color: '#4786FF', cursor: 'pointer' }}
													onClick={handlePolicyRedirect}
												>
													{signUpPageTranslate('text.policyTextPartFour')}
												</span>
											</Typography>
										</Box>
										<FormHelperText sx={{ marginLeft: '0px' }}>
											{errors.check?.message}
										</FormHelperText>
									</FormControl>
								</Grid>
							</Grid>

							{userData.loading ? (
								<Button
									sx={{
										marginTop: '24px',
										height: '44px',
										width: { xs: '100%', sm: '574px' },
										borderRadius: '8px',
									}}
									type="submit"
									variant="contained"
									color="primary"
								>
									<Oval
										height={24}
										width={24}
										color="#fff"
										wrapperStyle={{}}
										wrapperClass=""
										visible={true}
										ariaLabel="oval-loading"
										secondaryColor="#91B6FF"
										strokeWidth={5}
										strokeWidthSecondary={5}
									/>
								</Button>
							) : (
								<Button
									sx={{
										marginTop: '24px',
										height: '44px',
										width: { xs: '100%', sm: '574px' },
										borderRadius: '8px',
										textTransform: 'none',
										fontSize: '16px',
										fontWeight: '700',
									}}
									type="submit"
									variant="contained"
									color="primary"
								>
									{commonTranslate('buttons.signUp')}
								</Button>
							)}
						</form>
					</Grid>

					<Grid item sx={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
						<Typography>{signUpPageTranslate('text.alreadyHaveAnAccount')}</Typography>
						<Typography sx={{ color: '#4786FF', cursor: 'pointer' }} onClick={redirectToLogin}>
							{commonTranslate('buttons.logIn')}
						</Typography>
					</Grid>
				</Grid>
				<ErrorVerifyEmailModal
					isOpen={openErrorVerify}
					onConfirm={handleRestartVerify}
					onClose={handleActionErrorVerifyModal}
					description={modalsTranslate('errorVerifyEmail.sendingError') || ''}
				/>
			</Box>
		</>
	);
};
